import { engineComponent, Logger, EngineComponentFactory } from "neo-common";
import { Communication } from "neo-games";
import { ComponentTypes } from "../../Components/ComponentTypes";
import { UserControls } from "../Common/UserControls/UserControls";
import { HtmlController } from "../../HTMLComponents/HtmlController";
import { GameActions } from "../Common/Actions/GameActions";
import { ModuleManager } from "../../Managers/ModuleManager";
import { PickActions } from "../Common/Actions/PickActions";
import { PickController } from "../../GameComponents/Pick/PickController";
import { GameManager } from "../../Managers/GameManager";
import { GameData } from "../Common/Datas/GameData";
import { DataManager } from "../../Managers/DataManager";
import { PickView } from "../../GameComponents/Pick/PickView";
import { AppContext } from "../../../Common/AppContext";
import { StyxClientHostEvents } from "./FireBirdTypes";
import { ActionsTypes, ActionSet } from "../Common/Actions/ActionSet";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../../Utils/FrameTimer";

var GameModules = Communication.Game.GameModules;

@engineComponent(ComponentTypes.HtmlElement)
export class FireBirdPickUserControls extends UserControls {
    protected ledStripe: string;

    protected _controller: PickController;
    protected _terminalActions: ActionSet;

    private _userControls: HTMLElement;
    private _userControls_supreme: HTMLElement;
    private _pickCount: HTMLElement;
    private _pickMultiplier: HTMLElement;

    private _featureAutoStartTimer: FrameTimerEventEmitter;
    private _featureAutoStarted: boolean = false;
    private _featureAutoEndTimer: FrameTimerEventEmitter;
    private _featureAutoEnded: boolean = false;

    constructor() {
        super("pick");
    }

    create(node: Element): void {
        super.create(node);

        this.ledStripe = node.hasAttribute("LedStripe") ? node.getAttribute("LedStripe") : "255;255;255";

        this._terminalActions = new ActionSet(
            `${this._key}.terminalActions`,
            EngineComponentFactory.getComponentName(this),
        );

        this.registerActions();

        this._userControls = document.getElementById("pickUserControls");

        this._pickCount = document.getElementById("pickCount");
        if (this._pickCount == null) {
            if (DEBUG) {
                this.log("ERROR no pickCount");
            }
        }

        this._pickMultiplier = document.getElementById("pickMultiplier");
        if (this._pickMultiplier == null) {
            if (DEBUG) {
                this.log("ERROR no pickMultiplier");
            }
        }

        this.handleClientHostReload = this.handleClientHostReload.bind(this);

        // event 'reload' is fired when client host requests reload of actions (styx/firebird only)
        AppContext.clientHost.on(StyxClientHostEvents.Reload, this.handleClientHostReload);

        this._featureAutoStartTimer = CreateFrameTimer("featureAutoStartTimer");
        this._featureAutoStartTimer.on(FrameTimerEvents.Complete, this.featureAutoStartDelayed.bind(this));

        this._featureAutoEndTimer = CreateFrameTimer("featureAutoEndTimer");
        this._featureAutoEndTimer.on(FrameTimerEvents.Complete, this.featureAutoEndDelayed.bind(this));
    }

    private handleClientHostReload() {
        if (ModuleManager.gambleActive()) {
            return;
        }

        if (DEBUG) {
            Logger.debugGroup("FireBirdPickUserControls", `Event 'reload' arrived, actions updated...`);
        }

        if (ModuleManager.pickEnabled() || ModuleManager.pickActive()) {
            AppContext.clientHost.setLEDStripe(this.ledStripe, 0);
            AppContext.clientHost.actionsUpdated(this.getAllTerminalActions());
        }
    }

    public getAllTerminalActions(): ActionsTypes {
        return this._terminalActions.getAllActions();
    }

    addController(controller: PickController): void {
        this._controller = controller;
        this._controller.on(PickController.StartReceived, this.startReceived.bind(this));
        this._controller.on(PickController.SelectReceived, this.selectReceived.bind(this));
        this._controller.on(PickController.EndReceived, this.endReceived.bind(this));
        this._controller.on(PickController.UpdateCounters, this.updateCounter.bind(this));
        this._controller.on(PickController.UpdateCountersValues, this.updateCounterValues.bind(this));
        this._controller.on(PickController.SelectComplete, this.selectComplete.bind(this));
        this._controller.on(PickController.IntroFinished, this.introFinished.bind(this));
        this._controller.on(PickController.OutroFinished, this.outroFinished.bind(this));
    }

    registerActions(): void {
        this.actions.registerActions({
            [PickActions.ActionSpot + 0]: false,
            [PickActions.ActionSpot + 1]: false,
            [PickActions.ActionSpot + 2]: false,
            [PickActions.ActionSpot + 3]: false,
            [PickActions.ActionSpot + 4]: false,
            [PickActions.ActionSpot + 5]: false,
            [PickActions.ActionSpot + 6]: false,
            [PickActions.ActionSpot + 7]: false,
            [PickActions.ActionSpot + 8]: false,
            [PickActions.ActionSpot + 9]: false,
            [PickActions.ActionSpot + 10]: false,
            [PickActions.ActionSpot + 11]: false,
            [PickActions.ActionSpot + 12]: false,
            [PickActions.ActionSpot + 13]: false,
            [PickActions.ActionSpot + 14]: false,
        });
    }

    initReceived(): void {
        this._userControls_supreme = document.getElementById("userControls_supreme");

        super.initReceived();

        this._controller.initReceived();
        this._controller.disableLogo();

        if (GameManager.pickResponse.multiplier != null) {
            DataManager.updateGameDataFromModule(
                GameData.DataFreeSpinsMultiplier,
                GameManager.pickResponse.multiplier.toString(),
            );
            DataManager.updateDataElement(
                GameData.DataFreeSpinsMultiplier,
                GameManager.pickResponse.multiplier.toString(),
            );
        }

        if (this._userControls != null) {
            this._userControls.style.display = "none";
        }
        if (this._userControls != null && !this._userControls.classList.contains(HtmlController.DisabledClass)) {
            this._userControls.classList.add(HtmlController.DisabledClass);
        }
    }

    startReceived(): void {
        ModuleManager.outroModule(GameModules.Base);
    }

    selectReceived(): void {
        if (this._pickCount && this._pickCount.classList.contains("flash")) {
            this._pickCount.classList.remove("flash");
        }

        if (this._pickMultiplier && this._pickMultiplier.classList.contains("flash")) {
            this._pickMultiplier.classList.remove("flash");
        }
    }

    endReceived(): void {
        if (ModuleManager.freespinsEnabled()) {
            ModuleManager.enableFreespins();
            this.startFeatureAutoEnd();
        }
    }

    dispatchAction(action: string): void {
        if (ModuleManager.pickEnabled() || ModuleManager.pickActive()) {
            if (action === GameActions.ActionSpinOrStop) {
                return;
            }

            if (this.actions.getActionState(action)) {
                super.dispatchAction(action);

                if (action.substr(0, PickActions.ActionSpot.length) === PickActions.ActionSpot) {
                    let id: number = parseInt(action.substr(PickActions.ActionSpot.length, action.length));
                    this._controller.select(id);
                }
            }
            this.actions.disableAllActions();
            HtmlController.updateActionElements(this.getAllActions());
        }
    }

    start(): void {
        this.actions.disableAllActions();
        HtmlController.updateActionElements(this.getAllActions());
        this._controller.start();
    }

    back(): void {}

    end(): void {
        if (GameManager.pickResponse.count != null && GameManager.pickResponse.multiplier != null) {
            DataManager.updateGameDataFromModule(
                GameData.DataFreeSpinsCountMultiplier,
                GameManager.pickResponse.count.toString() + ";" + GameManager.pickResponse.multiplier.toString(),
            );
            DataManager.updateDataElement(
                GameData.DataFreeSpinsCountMultiplier,
                GameManager.pickResponse.count.toString() + ";" + GameManager.pickResponse.multiplier.toString(),
            );
        }
        this._controller.end();
    }

    initIntro(): void {
        AppContext.clientHost.setLEDStripe(this.ledStripe, 2);

        super.initIntro();
        this._controller.initIntro();
        this.prepareNewGameRound(true);

        if (GameManager.pickResponse.count != null) {
            DataManager.updateGameDataFromModule(
                GameData.DataFreeSpinsRemainingCount,
                GameManager.pickResponse.count.toString(),
            );
            DataManager.updateDataElement(
                GameData.DataFreeSpinsRemainingCount,
                GameManager.pickResponse.count.toString(),
            );
        }

        if (this._pickCount && this._pickCount.classList.contains("flash")) {
            this._pickCount.classList.remove("flash");
        }

        if (this._pickMultiplier && this._pickMultiplier.classList.contains("flash")) {
            this._pickMultiplier.classList.remove("flash");
        }

        if (this._userControls != null) {
            this._userControls.style.display = "block";
        }

        if (this._userControls_supreme != null) {
            this._userControls_supreme.style.display = "block";
        }

        if (this._userControls != null && this._userControls.classList.contains(HtmlController.DisabledClass)) {
            this._userControls.classList.remove(HtmlController.DisabledClass);
        }

        if (
            this._userControls_supreme != null &&
            this._userControls_supreme.classList.contains(HtmlController.DisabledClass)
        ) {
            this._userControls_supreme.classList.remove(HtmlController.DisabledClass);
        }

        this.startFeatureAutoStart();
    }

    intro(long: boolean = false): void {
        AppContext.clientHost.setLEDStripe(this.ledStripe, 2);

        this._controller.intro();

        if (GameManager.pickResponse.count != null) {
            DataManager.updateGameDataFromModule(
                GameData.DataFreeSpinsRemainingCount,
                GameManager.pickResponse.count.toString(),
            );
            DataManager.updateDataElement(
                GameData.DataFreeSpinsRemainingCount,
                GameManager.pickResponse.count.toString(),
            );
        }

        if (GameManager.pickResponse.multiplier != null) {
            DataManager.updateGameDataFromModule(
                GameData.DataFreeSpinsMultiplier,
                GameManager.pickResponse.multiplier.toString(),
            );
            DataManager.updateDataElement(
                GameData.DataFreeSpinsMultiplier,
                GameManager.pickResponse.multiplier.toString(),
            );
        }

        if (this._pickCount && this._pickCount.classList.contains("flash")) {
            this._pickCount.classList.remove("flash");
        }

        if (this._pickMultiplier && this._pickMultiplier.classList.contains("flash")) {
            this._pickMultiplier.classList.remove("flash");
        }
    }

    introFinished(): void {
        this.prepareNewGameRound();

        if (this._userControls != null) {
            this._userControls.style.display = "block";
        }

        if (this._userControls_supreme != null) {
            this._userControls_supreme.style.display = "block";
        }

        if (this._userControls != null && this._userControls.classList.contains(HtmlController.DisabledClass)) {
            this._userControls.classList.remove(HtmlController.DisabledClass);
        }

        if (
            this._userControls_supreme != null &&
            this._userControls_supreme.classList.contains(HtmlController.DisabledClass)
        ) {
            this._userControls_supreme.classList.remove(HtmlController.DisabledClass);
        }

        this.startFeatureAutoStart();
    }

    outro(): void {
        this._controller.outro();

        if (this._userControls != null && !this._userControls.classList.contains(HtmlController.DisabledClass)) {
            this._userControls.classList.add(HtmlController.DisabledClass);
        }
    }

    outroFinished(): void {
        ModuleManager.introModule(GameModules.Freespins, true);
        if (this._userControls != null) {
            this._userControls.style.display = "none";
        }
    }

    prepareNewGameRound(init: boolean = false): void {
        this.actions.changeActionStates({
            [PickActions.ActionSpot + 0]: false,
            [PickActions.ActionSpot + 1]: false,
            [PickActions.ActionSpot + 2]: false,
            [PickActions.ActionSpot + 3]: false,
            [PickActions.ActionSpot + 4]: false,
            [PickActions.ActionSpot + 5]: false,
            [PickActions.ActionSpot + 6]: false,
            [PickActions.ActionSpot + 7]: false,
            [PickActions.ActionSpot + 8]: false,
            [PickActions.ActionSpot + 9]: false,
            [PickActions.ActionSpot + 10]: false,
            [PickActions.ActionSpot + 11]: false,
            [PickActions.ActionSpot + 12]: false,
            [PickActions.ActionSpot + 13]: false,
            [PickActions.ActionSpot + 14]: false,
        });

        if (
            GameManager.pickResponse != null &&
            GameManager.pickResponse.round != null &&
            GameManager.pickResponse.round.spots != null
        ) {
            if (GameManager.pickResponse.round.remainingPicks === 0) {
                if (!ModuleManager.freespinsEnabled()) {
                    this.end();
                } else {
                    if (GameManager.pickResponse.count != null && GameManager.pickResponse.multiplier != null) {
                        DataManager.updateGameDataFromModule(
                            GameData.DataFreeSpinsCountMultiplier,
                            GameManager.pickResponse.count.toString() +
                                ";" +
                                GameManager.pickResponse.multiplier.toString(),
                        );
                        DataManager.updateDataElement(
                            GameData.DataFreeSpinsCountMultiplier,
                            GameManager.pickResponse.count.toString() +
                                ";" +
                                GameManager.pickResponse.multiplier.toString(),
                        );
                    }
                    ModuleManager.enableFreespins();
                    this.startFeatureAutoEnd();
                }
                return;
            }

            for (let key in GameManager.pickResponse.round.spots) {
                let id: number = GameManager.pickResponse.round.spots[key].id;
                let status: string = GameManager.pickResponse.round.spots[key].status.toString();

                if (id != null && status != null && status === "enabled") {
                    this.actions.changeActionState(PickActions.ActionSpot + id, true);
                }
            }
        }

        HtmlController.updateActionElements(this.getAllActions());
    }

    updateCounter(): void {
        if (PickView.ActualValueType != "") {
            if (PickView.ActualValueType === "count") {
                if (this._pickCount && !this._pickCount.classList.contains("flash")) {
                    this._pickCount.classList.add("flash");
                }
            } else if (PickView.ActualValueType === "multiplier") {
                if (this._pickMultiplier && !this._pickMultiplier.classList.contains("flash")) {
                    this._pickMultiplier.classList.add("flash");
                }
            }
        }
    }

    updateCounterValues(): void {
        if (PickView.ActualValue != -1 && PickView.ActualValueType != "") {
            let count: number;
            let multiplier: number;

            if (PickView.ActualValueType === "count") {
                count = parseInt(DataManager.getDataById(GameData.DataFreeSpinsRemainingCount)) + PickView.ActualValue;
                DataManager.updateGameDataFromModule(GameData.DataFreeSpinsRemainingCount, count.toString());
                DataManager.updateDataElement(GameData.DataFreeSpinsRemainingCount, count.toString());
            } else if (PickView.ActualValueType === "multiplier") {
                multiplier = parseInt(DataManager.getDataById(GameData.DataFreeSpinsMultiplier)) + PickView.ActualValue;
                DataManager.updateGameDataFromModule(GameData.DataFreeSpinsMultiplier, multiplier.toString());
                DataManager.updateDataElement(GameData.DataFreeSpinsMultiplier, multiplier.toString());
            }
        }
    }

    selectComplete(): void {
        this.prepareNewGameRound();

        this.startFeatureAutoStart();
    }

    enable(): void {
        this.start();
    }

    disable(): void {
        this.actions.disableAllActions();
        HtmlController.updateActionElements(this.getAllActions());
    }

    /**
     * Start feature if enabled.
     */
    private featureAutoStartDelayed(): void {
        this._featureAutoStartTimer.stop();

        if (ModuleManager.pickActive()) {
            if (AppContext.gameConfiguration.FeatureAutoPick != null && AppContext.gameConfiguration.FeatureAutoPick) {
                this._featureAutoStarted = true;
                this.pickRandom();
            }
        }
    }

    /**
     * Dispatch action Back by timer.
     */
    private featureAutoEndDelayed(): void {
        this._featureAutoEndTimer.stop();
        this._featureAutoEnded = true;
        ModuleManager.startFreespins();

        if (DEBUG) {
            this.log("INFO featureAutoEndDelayed ActionBack");
        }
    }

    /**
     * Auto start feature.
     * Duration is set to preDuration + duration from configuration.
     * @param {number} preDuration
     */
    protected startFeatureAutoStart(preDuration: number = 0): void {
        if (AppContext.gameConfiguration.FeatureAutoPlay != null && AppContext.gameConfiguration.FeatureAutoPlay > 0) {
            this._featureAutoStartTimer.start(preDuration + AppContext.gameConfiguration.FeatureAutoPlay);
        }
    }

    /**
     * Auto end feature.
     * Duration is set to preDuration + duration from configuration.
     * @param {number} preDuration
     */
    protected startFeatureAutoEnd(preDuration: number = 0): void {
        if (AppContext.gameConfiguration.FeatureAutoPlay != null && AppContext.gameConfiguration.FeatureAutoPlay > 0) {
            this._featureAutoEndTimer.start(preDuration + AppContext.gameConfiguration.FeatureAutoPlay);
        }
    }

    /**
     *
     */
    protected pickRandom(): void {
        if (
            GameManager.pickResponse != null &&
            GameManager.pickResponse.round != null &&
            GameManager.pickResponse.round.spots != null
        ) {
            for (let key in GameManager.pickResponse.round.spots) {
                let id: number = GameManager.pickResponse.round.spots[key].id;
                let status: string = GameManager.pickResponse.round.spots[key].status.toString();

                if (id != null && status != null && status === "enabled") {
                    this.dispatchAction(PickActions.ActionSpot + id);
                    return;
                }
            }
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/Channels/FireBird/FireBirdPickUserControls.ts
 **/